import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiCalendar,
  FiShield,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiFileText,
  FiEye,
  FiDownload,
  FiRefreshCw,
  FiTruck,
  FiInfo
} from 'react-icons/fi';

const Insurance = () => {
  const { user } = useAuth();
  const [cars, setCars] = useState([]);
  const [insuranceRecords, setInsuranceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carsLoading, setCarsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [formData, setFormData] = useState({
    carId: '',
    policyNumber: '',
    insuranceCompany: '',
    policyType: 'Tous Risques',
    startDate: '',
    endDate: '',
    premium: '',
    coverage: '',
    deductible: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Debug: Log cars whenever they change
  useEffect(() => {
    // console.log('Cars state updated:', cars);
    // console.log('Number of cars loaded:', cars.length);
  }, [cars]);

  // Check authentication
  useEffect(() => {
    if (!user) {
      console.log('No user found, redirecting to login');
      return;
    }
    console.log('User authenticated:', user.email, 'Role:', user.role);

    // Check if token exists
    const token = localStorage.getItem('token');
    console.log('Token exists:', !!token);
    if (token) {
      console.log('Token length:', token.length);
      console.log('Token starts with:', token.substring(0, 20) + '...');
    }
  }, [user]);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || 'null');

    console.log('=== AUTH STATUS CHECK ===');
    console.log('Token exists:', !!token);
    console.log('Token length:', token ? token.length : 0);
    console.log('User in localStorage:', user);
    console.log('User from context:', user);
    console.log('========================');

    if (!token) {
      toast.error('Aucun token trouvé. Veuillez vous reconnecter.');
      return false;
    }

    if (!user) {
      toast.error('Aucun utilisateur trouvé. Veuillez vous reconnecter.');
      return false;
    }

    toast.success('Authentification OK. Token et utilisateur trouvés.');
    return true;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setCarsLoading(true);

      // Always try to fetch cars first
      try {
        const carsRes = await api.get('/api/cars');
        setCars(carsRes.data.data);
        console.log('Cars loaded successfully:', carsRes.data.data);
        setCarsLoading(false);
      } catch (carsError) {
        console.error('Error fetching cars:', carsError);
        if (carsError.response?.status === 401) {
          toast.error('Token expiré ou invalide. Veuillez vous reconnecter.');
        } else if (carsError.response?.status === 403) {
          toast.error('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
        } else if (carsError.response?.status === 404) {
          toast.error('Endpoint des voitures non trouvé.');
        } else {
          toast.error(`Erreur lors du chargement des voitures: ${carsError.message}`);
        }
        setCars([]);
        setCarsLoading(false);
      }

      // Try to fetch insurance data
      try {
        const insuranceRes = await api.get('/api/insurance');
        setInsuranceRecords(insuranceRes.data.data || []);
      } catch (insuranceError) {
        console.error('Error fetching insurance:', insuranceError);
        // If insurance API doesn't exist yet, create mock data
        setInsuranceRecords(generateMockInsuranceData());
        console.log('Using mock insurance data');
      }
    } catch (error) {
      console.error('General error in fetchData:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMockInsuranceData = () => {
    return [
      {
        _id: '1',
        carId: '1',
        carInfo: { brand: 'Toyota', model: 'Camry', licensePlate: 'ABC-123' },
        policyNumber: 'POL-001-2024',
        insuranceCompany: 'Allianz',
        policyType: 'Tous Risques',
        startDate: '2024-01-01',
        endDate: '2024-12-31',
        premium: 1200.00,
        coverage: 'Couverture complète avec assistance routière',
        deductible: 500.00,
        status: 'Active',
        notes: 'Police premium avec avantages supplémentaires'
      },
      {
        _id: '2',
        carId: '2',
        carInfo: { brand: 'Honda', model: 'Civic', licensePlate: 'XYZ-789' },
        policyNumber: 'POL-002-2024',
        insuranceCompany: 'AXA',
        policyType: 'Tiers',
        startDate: '2024-03-01',
        endDate: '2025-02-28',
        premium: 800.00,
        coverage: 'Couverture tiers de base',
        deductible: 1000.00,
        status: 'Active',
        notes: 'Couverture standard pour usage quotidien'
      }
    ];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.carId) {
      toast.error('Veuillez sélectionner une voiture');
      return;
    }
    if (!formData.policyNumber) {
      toast.error('Veuillez saisir le numéro de police');
      return;
    }
    if (!formData.insuranceCompany) {
      toast.error('Veuillez saisir la compagnie d\'assurance');
      return;
    }
    if (!formData.startDate) {
      toast.error('Veuillez saisir la date de début');
      return;
    }
    if (!formData.endDate) {
      toast.error('Veuillez saisir la date de fin');
      return;
    }
    if (!formData.premium) {
      toast.error('Veuillez saisir la prime');
      return;
    }

    // Validate dates
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      toast.error('La date de fin doit être postérieure à la date de début');
      return;
    }

    try {
      if (showEditModal) {
        await api.put(`/api/insurance/${selectedInsurance._id}`, formData);
        toast.success('Assurance mise à jour avec succès');
      } else {
        await api.post('/api/insurance', formData);
        toast.success('Assurance ajoutée avec succès');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setFormData({
        carId: '',
        policyNumber: '',
        insuranceCompany: '',
        policyType: 'Comprehensive',
        startDate: '',
        endDate: '',
        premium: '',
        coverage: '',
        deductible: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error in handleSubmit:', error);

      if (error.response?.status === 401) {
        toast.error('Token expiré ou invalide. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        toast.error('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
      } else if (error.response?.status === 404) {
        toast.error('API d\'assurance non trouvée. Le backend n\'est pas encore configuré.');
      } else if (error.response?.status === 500) {
        toast.error('Erreur serveur. Vérifiez que le backend est en cours d\'exécution.');
      } else if (error.code === 'NETWORK_ERROR') {
        toast.error('Erreur de connexion. Vérifiez que le serveur est accessible.');
      } else {
        toast.error(`Erreur lors de l'opération: ${error.message || 'Erreur inconnue'}`);
      }
    }
  };

  const handleEdit = (insurance) => {
    setSelectedInsurance(insurance);
    setFormData({
      carId: insurance.carId,
      policyNumber: insurance.policyNumber,
      insuranceCompany: insurance.insuranceCompany,
      policyType: insurance.policyType,
      startDate: insurance.startDate,
      endDate: insurance.endDate,
      premium: insurance.premium,
      coverage: insurance.coverage,
      deductible: insurance.deductible,
      notes: insurance.notes
    });
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (!selectedInsurance) {
      toast.error('Aucune assurance sélectionnée');
      return;
    }

    try {
      await api.delete(`/api/insurance/${selectedInsurance._id}`);
      toast.success('Assurance supprimée avec succès');
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      console.error('Error in handleDelete:', error);

      if (error.response?.status === 401) {
        toast.error('Token expiré ou invalide. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        toast.error('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
      } else if (error.response?.status === 404) {
        toast.error('Assurance non trouvée ou déjà supprimée.');
      } else if (error.response?.status === 500) {
        toast.error('Erreur serveur lors de la suppression.');
      } else if (error.code === 'NETWORK_ERROR') {
        toast.error('Erreur de connexion lors de la suppression.');
      } else {
        toast.error(`Erreur lors de la suppression: ${error.message || 'Erreur inconnue'}`);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border border-green-200 dark:border-green-800';
      case 'Expired': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border border-red-200 dark:border-red-800';
      case 'Expiring Soon': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Active': return 'ACTIF';
      case 'Expired': return 'EXPIRÉ';
      case 'Expiring Soon': return 'EXPIRE BIENTÔT';
      default: return 'INCONNU';
    }
  };

  const calculateStatus = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const daysUntilExpiry = Math.ceil((end - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return 'Expired';
    if (daysUntilExpiry <= 30) return 'Expiring Soon';
    return 'Active';
  };

  const filteredInsurance = insuranceRecords.filter(insurance => {
    const matchesSearch =
      insurance.policyNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insurance.insuranceCompany.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (insurance.carId?.brand || insurance.carInfo?.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (insurance.carId?.model || insurance.carInfo?.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (insurance.carId?.licensePlate || insurance.carInfo?.licensePlate || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || calculateStatus(insurance.endDate) === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getExpiringSoonCount = () => {
    return insuranceRecords.filter(insurance => calculateStatus(insurance.endDate) === 'Expiring Soon').length;
  };

  const getExpiredCount = () => {
    return insuranceRecords.filter(insurance => calculateStatus(insurance.endDate) === 'Expired').length;
  };

  const testInsuranceAPI = async () => {
    try {
      console.log('Testing insurance API endpoints...');

      // Test GET endpoint
      try {
        const response = await api.get('/api/insurance');
        console.log('GET /api/insurance - Success:', response.status);
        toast.success('API d\'assurance accessible - GET');
      } catch (error) {
        console.log('GET /api/insurance - Failed:', error.response?.status, error.message);
        if (error.response?.status === 404) {
          toast.error('API d\'assurance non trouvée (404) - Le backend n\'est pas configuré');
        } else {
          toast.error(`GET /api/insurance échoué: ${error.response?.status || error.message}`);
        }
      }

      // Test POST endpoint with dummy data
      try {
        const dummyData = {
          carId: cars[0]?._id || 'test',
          policyNumber: 'TEST-001',
          insuranceCompany: 'Test Company',
          policyType: 'Comprehensive',
          startDate: '2024-01-01',
          endDate: '2024-12-31',
          premium: 100
        };
        const response = await api.post('/api/insurance', dummyData);
        console.log('POST /api/insurance - Success:', response.status);
        toast.success('API d\'assurance accessible - POST');
      } catch (error) {
        console.log('POST /api/insurance - Failed:', error.response?.status, error.message);
        if (error.response?.status === 404) {
          toast.error('API d\'assurance non trouvée (404) - Le backend n\'est pas configuré');
        } else if (error.response?.status === 401) {
          toast.error('Authentification requise pour POST /api/insurance');
        } else {
          toast.error(`POST /api/insurance échoué: ${error.response?.status || error.message}`);
        }
      }

    } catch (error) {
      console.error('Error testing insurance API:', error);
      toast.error('Erreur lors du test de l\'API d\'assurance');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Suivi des Assurances</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Gérez les polices d'assurance de votre flotte</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn bg-brand-green hover:bg-green-700 text-white shadow-md flex items-center gap-2 transition-all px-4 py-2 rounded-lg text-sm w-fit"
          title="Ajouter une nouvelle police d'assurance"
        >
          <FiPlus className="w-4 h-4" />
          <span className="hidden sm:inline">Ajouter une Assurance</span>
          <span className="sm:hidden">Ajouter</span>
        </button>
      </div>



      {/* API Status Warning */}
      {insuranceRecords.length > 0 && insuranceRecords[0]._id === '1' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <FiInfo className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">Mode Démonstration</span>
          </div>
          <div className="mt-2 text-sm text-blue-700">
            <p>⚠️ L'API d'assurance n'est pas encore configurée sur le backend.</p>
            <p>Cette page utilise des données de démonstration. Les opérations d'ajout/modification/suppression ne fonctionneront pas jusqu'à ce que l'API soit implémentée.</p>
            <p>Utilisez le bouton "Tester API Assurance" ci-dessus pour vérifier le statut de l'API.</p>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-white dark:bg-gray-800 p-3 sm:p-6 rounded-xl shadow-sm border-t-4 border-primary-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-primary-100 dark:bg-primary-900/40 rounded-full">
              <FiShield className="w-5 h-5 sm:w-8 sm:h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{insuranceRecords.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3 sm:p-6 rounded-xl shadow-sm border-t-4 border-brand-green hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-green-100 dark:bg-green-900/40 rounded-full">
              <FiCheckCircle className="w-5 h-5 sm:w-8 sm:h-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Actives</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {insuranceRecords.filter(insurance => calculateStatus(insurance.endDate) === 'Active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3 sm:p-6 rounded-xl shadow-sm border-t-4 border-yellow-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-yellow-100 dark:bg-yellow-900/40 rounded-full">
              <FiAlertTriangle className="w-5 h-5 sm:w-8 sm:h-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Expirent</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{getExpiringSoonCount()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3 sm:p-6 rounded-xl shadow-sm border-t-4 border-red-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-red-100 dark:bg-red-900/40 rounded-full">
              <FiClock className="w-5 h-5 sm:w-8 sm:h-8 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Expirées</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{getExpiredCount()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow mb-4 sm:mb-6">
        <div className="flex flex-col gap-3">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm flex-1 sm:flex-none"
            >
              <option value="all">Tous</option>
              <option value="Active">Actives</option>
              <option value="Expiring Soon">Expirent</option>
              <option value="Expired">Expirées</option>
            </select>

            <button
              onClick={() => fetchData()}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              title="Actualiser les données"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>

            <button
              onClick={async () => {
                try {
                  setCarsLoading(true);
                  const carsRes = await api.get('/api/cars');
                  setCars(carsRes.data.data);
                  toast.success(`Voitures rechargées: ${carsRes.data.data.length} trouvées`);
                } catch (error) {
                  toast.error('Erreur lors du rechargement des voitures');
                  console.error('Error refreshing cars:', error);
                } finally {
                  setCarsLoading(false);
                }
              }}
              disabled={carsLoading}
              className={`px-3 py-2 rounded-lg flex items-center space-x-1 text-sm ${carsLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-200 text-blue-700 hover:bg-blue-300'
                }`}
              title="Recharger la liste des voitures"
            >
              {carsLoading ? (
                <FiRefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <FiTruck className="w-4 h-4" />
              )}
              <span className="ml-1 hidden sm:inline">
                {carsLoading ? 'Chargement...' : 'Recharger'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Insurance Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Voiture
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Police
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Compagnie
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Expiration
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredInsurance.map((insurance) => {
                const status = calculateStatus(insurance.endDate);
                return (
                  <tr key={insurance._id} className="hover:bg-gray-50">

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {insurance.carId?.brand} {insurance.carId?.model}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <FiTruck className="w-3 h-3" /> {insurance.carId?.licensePlate}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{insurance.policyNumber}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{insurance.insuranceCompany}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{insurance.policyType}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(insurance.endDate).toLocaleDateString('fr-FR')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {status === 'Expired' ? 'Expiré' :
                          status === 'Expiring Soon' ? 'Expire bientôt' : 'Actif'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(status)}`}>
                        {getStatusText(status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(insurance)}
                          className="p-2 text-gray-500 hover:text-brand-green hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                          title="Modifier l'assurance"
                        >
                          <FiEdit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInsurance(insurance);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Supprimer l'assurance"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {showEditModal ? 'Modifier l\'Assurance' : 'Ajouter une Assurance'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Voiture *
                  </label>
                  <select
                    required
                    value={formData.carId}
                    onChange={(e) => setFormData({ ...formData, carId: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    disabled={carsLoading}
                  >
                    <option value="">
                      {carsLoading
                        ? 'Chargement des voitures...'
                        : cars.length === 0
                          ? 'Aucune voiture disponible'
                          : 'Sélectionner une voiture'
                      }
                    </option>
                    {cars.map(car => (
                      <option key={car._id} value={car._id}>
                        {car.brand} {car.model} - {car.licensePlate}
                      </option>
                    ))}
                  </select>
                  {carsLoading && (
                    <p className="text-sm text-blue-600 mt-1">Chargement des voitures...</p>
                  )}
                  {!carsLoading && cars.length === 0 && (
                    <p className="text-sm text-red-600 mt-1">
                      ⚠️ Aucune voiture trouvée. Utilisez le bouton "Recharger Voitures" ci-dessus.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de Police *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.policyNumber}
                    onChange={(e) => setFormData({ ...formData, policyNumber: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Compagnie d'Assurance *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.insuranceCompany}
                    onChange={(e) => setFormData({ ...formData, insuranceCompany: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type de Police *
                  </label>
                  <select
                    required
                    value={formData.policyType}
                    onChange={(e) => setFormData({ ...formData, policyType: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Comprehensive">Tous Risques</option>
                    <option value="Third Party">Tiers</option>
                    <option value="Third Party Fire & Theft">Tiers, Incendie et Vol</option>
                    <option value="Liability Only">Responsabilité Civile</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de Début *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de Fin *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prime (MAD) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.premium}
                    onChange={(e) => setFormData({ ...formData, premium: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Franchise (MAD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.deductible}
                    onChange={(e) => setFormData({ ...formData, deductible: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Couverture
                </label>
                <textarea
                  value={formData.coverage}
                  onChange={(e) => setFormData({ ...formData, coverage: e.target.value })}
                  rows="3"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Détails de la couverture d'assurance..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="2"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Notes supplémentaires..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  {showEditModal ? 'Mettre à jour' : 'Ajouter l\'Assurance'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setFormData({
                      carId: '',
                      policyNumber: '',
                      insuranceCompany: '',
                      policyType: 'Tous Risques',
                      startDate: '',
                      endDate: '',
                      premium: '',
                      coverage: '',
                      deductible: '',
                      notes: ''
                    });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <div className="text-center">
              <FiAlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Supprimer l'Assurance</h2>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer cette assurance ? Cette action est irréversible.
              </p>
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Oui, Supprimer
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Insurance;
